import { OUTLOOK_COPY, TARGET_OUTLOOK_PATH } from "@/revenue-intelligence/target-outlook/copy";
import {
  Coins,
  Handshake,
  History,
  Home,
  LineChart,
  LucideIcon,
  Percent,
  PlugZap,
  Target,
  User,
  Users,
  PanelLeftClose,
  PanelLeftOpen,
  Activity,
  Bot,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { getScopeRoute } from "@/lib/routes";
import { Scope, SignedInUser } from "@revelate/types";
import { PropsWithChildren, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  buildRevenueIntelligenceContextHref,
  REVENUE_INTELLIGENCE_AGENTS_PATH,
  isRevenueIntelligenceHomePath,
  isRevenueIntelligenceProductPath,
  REVENUE_INTELLIGENCE_FILTER_CONTEXT_CHANGED_EVENT,
  STATE_OF_SALES_PATH,
} from "@/revenue-intelligence/lib/revenue-intelligence-routes";
import { hasRevenueIntelligenceProductAccess } from "@/revenue-intelligence/lib/revenue-intelligence-product-access";
import useSWR from "swr";
import {
  getNewPersistentRevenueIntelligenceSignalCount,
  REVENUE_INTELLIGENCE_SIGNALS_CHANGED_EVENT,
} from "@/revenue-intelligence/lib/revenue-intelligence-signals";

const linkBase = "flex h-8 items-center gap-3 rounded-[6px] px-3 transition-colors";
const linkActive = "bg-gray-200";
const linkInactive = "text-gray-700 font-normal hover:text-[#1E1E1E] hover:bg-gray-100";
const linkIcon = "h-3.5 w-3.5"

interface AppSidebarProps {
  currentUser: SignedInUser | null;
  currentUserScope: Scope | null;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

// TODO: Needs desktop and mobile versions
// Encapsulate common sections then wrap with desktop or mobile container
export function AppSidebar({
  currentUser,
  currentUserScope,
  collapsed = false,
  onToggleCollapsed,
}: AppSidebarProps) {
  const [location] = useLocation();
  const [, setFilterContextVersion] = useState(0);
  useEffect(() => {
    const refreshFilterContext = () => setFilterContextVersion(version => version + 1);
    window.addEventListener(REVENUE_INTELLIGENCE_FILTER_CONTEXT_CHANGED_EVENT, refreshFilterContext);
    return () => window.removeEventListener(REVENUE_INTELLIGENCE_FILTER_CONTEXT_CHANGED_EVENT, refreshFilterContext);
  }, []);
  const currentLocation = typeof window === "undefined"
    ? location
    : `${window.location.pathname}${window.location.search}`;
  const pathname = currentLocation.split("?")[0];
  const revenueIntelligenceHomeHref = buildRevenueIntelligenceContextHref("/home", currentLocation);
  const revenueIntelligenceAgentsHref = buildRevenueIntelligenceContextHref(REVENUE_INTELLIGENCE_AGENTS_PATH, currentLocation);
  const stateOfSalesHref = buildRevenueIntelligenceContextHref(STATE_OF_SALES_PATH, currentLocation);
  const managementReviewHref = buildRevenueIntelligenceContextHref("/revenue-intelligence/management-review", currentLocation);
  const revenueIntelligenceHistoryHref = buildRevenueIntelligenceContextHref("/revenue-intelligence/history", currentLocation);

  // Route matching
  const isReportsRoute = location.startsWith("/reports");
  const isPlansRoute = location.startsWith("/plans");
  const isQuotasRoute = location.startsWith("/quotas");
  const isDealsRoute = location.startsWith("/deals");
  const isUsersRoute = location.startsWith("/users");
  const isTeamsRoute = location.startsWith("/teams");
  const isCurrenciesRoute = location.startsWith("/currencies");
  const isIntegrationsRoute = location.startsWith("/integrations");
  const isRevenueIntelligenceRoute = isRevenueIntelligenceProductPath(pathname);
  const isRevenueIntelligenceHomeRoute = isRevenueIntelligenceHomePath(pathname);
  const isRevenueIntelligenceAgentsRoute = pathname === REVENUE_INTELLIGENCE_AGENTS_PATH;
  const isRevenueIntelligenceHistoryRoute = pathname.startsWith("/revenue-intelligence/history");
  const isRevenueIntelligenceStateOfSalesV2Route = pathname === STATE_OF_SALES_PATH
    || pathname.startsWith("/revenue-intelligence/state-of-sales-v2");
  const isRevenueIntelligenceManagementReviewRoute = pathname === "/revenue-intelligence/management-review";

  const isCommissionDashboardRoute =
    !isPlansRoute &&
    !isReportsRoute &&
    !isQuotasRoute &&
    !isUsersRoute &&
    !isCurrenciesRoute &&
    !isIntegrationsRoute &&
    !isDealsRoute &&
    !isTeamsRoute &&
    !isRevenueIntelligenceRoute;

  // Role
  const isEmployee = currentUser?.role === "employee"
  // const isManager = currentUser?.role === "manager";
  const isAdmin = currentUser?.role === "admin";
  const isSuperAdmin = currentUser?.role === "super_admin" || currentUser?.is_super_admin || false;

  // Access
  const showCommissionSection = currentUser?.company?.product_access_commissions || isSuperAdmin;
  const showRevenueIntelligenceSection = hasRevenueIntelligenceProductAccess(currentUser);
  const showDataSection = isAdmin || isSuperAdmin;
  const canReadCompanySignals = showRevenueIntelligenceSection && (isAdmin || isSuperAdmin);
  const { data: newSignalCount = 0, mutate: refreshNewSignalCount } = useSWR(
    canReadCompanySignals && currentUser?.company_id
      ? ["revenue-intelligence-new-signal-count", currentUser.company_id]
      : null,
    () => getNewPersistentRevenueIntelligenceSignalCount({
      companyId: String(currentUser?.company_id),
    }),
    { revalidateOnFocus: false, revalidateOnReconnect: true },
  );

  useEffect(() => {
    const refresh = () => void refreshNewSignalCount();
    window.addEventListener(REVENUE_INTELLIGENCE_SIGNALS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(REVENUE_INTELLIGENCE_SIGNALS_CHANGED_EVENT, refresh);
  }, [refreshNewSignalCount]);

  return (
    <div className="flex flex-col gap-2">
      <div className={cn(
        "group/sidebar-toggle relative h-14 lg:h-[60px] flex flex-row items-center",
        collapsed ? "justify-start pl-5 pr-2" : "justify-between pl-5 pr-2",
      )}>
        {collapsed ? (
          <div className="relative h-6 w-6">
            <img
              src="/revelate-mark-light.svg"
              alt="Logo"
              className="absolute inset-0 h-6 w-6 transition-opacity group-hover/sidebar-toggle:opacity-0"
            />
            {onToggleCollapsed && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onToggleCollapsed}
                title="Expand navigation"
                className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 bg-transparent text-gray-700 opacity-0 hover:bg-gray-100 hover:text-gray-900 md:inline-flex group-hover/sidebar-toggle:opacity-100"
              >
                <PanelLeftOpen className="h-4 w-4" />
                <span className="sr-only">Expand navigation</span>
              </Button>
            )}
          </div>
        ) : (
          <>
            <img
              src="/revelate-logo.svg"
              alt="Logo"
              className="w-[130px]"
            />
            {onToggleCollapsed && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onToggleCollapsed}
                title="Collapse navigation"
                className="hidden md:inline-flex"
              >
                <PanelLeftClose className="h-4 w-4" />
                <span className="sr-only">Collapse navigation</span>
              </Button>
            )}
          </>
        )}
      </div>

      <div className="flex-1 w-full">
        <nav className={cn(
          "flex-1 grid items-start px-2 text-xs font-medium mt-2"
        )}>
          {showCommissionSection && (
            <SidebarSection title="Commissions" collapsed={collapsed}>
              <SidebarLink
                label="Dashboard"
                icon={Home}
                href={getScopeRoute(
                  "dashboard",
                  isEmployee ? currentUserScope : null,
                  ["company", "team", "user"]
                )}
                isActive={isCommissionDashboardRoute}
                collapsed={collapsed}
              />
              <SidebarLink
                label="Reports"
                icon={LineChart}
                href={getScopeRoute("reports", null, [
                  "user",
                  "team",
                  "company",
                ])}
                isActive={isReportsRoute}
                collapsed={collapsed}
              />
              <SidebarLink
                label="Compensation Plans"
                icon={Percent}
                href={getScopeRoute("plans", null, ["plan"])}
                isActive={isPlansRoute}
                visible={isSuperAdmin || isAdmin}
                collapsed={collapsed}
              />
              <SidebarLink
                label="Sales Quotas"
                icon={Target}
                href={getScopeRoute("quotas", null, ["quota"])}
                isActive={isQuotasRoute}
                visible={isSuperAdmin || isAdmin}
                collapsed={collapsed}
              />
            </SidebarSection>
          )}

          {/* Revenue Intelligence */}
          {showRevenueIntelligenceSection && (
            <SidebarSection title="Revenue Intelligence" collapsed={collapsed}>
              <SidebarLink
                label="Home"
                icon={Home}
                href={revenueIntelligenceHomeHref}
                isActive={isRevenueIntelligenceHomeRoute}
                indicator={newSignalCount > 0}
                collapsed={collapsed}
              />
              <SidebarLink
                label="Agents"
                icon={Bot}
                href={revenueIntelligenceAgentsHref}
                isActive={isRevenueIntelligenceAgentsRoute}
                collapsed={collapsed}
              />
              <SidebarLink
                label="State of Sales"
                icon={Activity}
                href={stateOfSalesHref}
                isActive={isRevenueIntelligenceStateOfSalesV2Route}
                collapsed={collapsed}
              />
              <SidebarLink
                label="Management Review"
                icon={LineChart}
                href={managementReviewHref}
                isActive={isRevenueIntelligenceManagementReviewRoute}
                collapsed={collapsed}
              />
              <SidebarLink
                label={OUTLOOK_COPY.title}
                icon={Target}
                href={TARGET_OUTLOOK_PATH}
                isActive={pathname === TARGET_OUTLOOK_PATH}
                visible={currentUser?.company_id === 13}
                collapsed={collapsed}
              />
              <SidebarLink
                label="History"
                icon={History}
                href={revenueIntelligenceHistoryHref}
                isActive={isRevenueIntelligenceHistoryRoute}
                visible={isSuperAdmin}
                collapsed={collapsed}
              />
            </SidebarSection>
          )}

          {showDataSection && (
            <SidebarSection title="Data" collapsed={collapsed}>
              <SidebarLink
                label="Deals"
                icon={Handshake}
                href={getScopeRoute("deals", null, ["deal"])}
                isActive={isDealsRoute}
                visible={isAdmin || isSuperAdmin}
                collapsed={collapsed}
              />
              <SidebarLink
                label="Users"
                icon={User}
                href={getScopeRoute("users", null, ["user"])}
                isActive={isUsersRoute}
                visible={!isEmployee}
                collapsed={collapsed}
              />
              <SidebarLink
                label="Teams"
                icon={Users}
                href={getScopeRoute("teams", null, ["team"])}
                isActive={isTeamsRoute}
                visible={isAdmin || isSuperAdmin}
                collapsed={collapsed}
              />
              <SidebarLink
                label="Currencies"
                icon={Coins}
                href={getScopeRoute("currencies", null, ["currency"])}
                isActive={isCurrenciesRoute}
                visible={isAdmin || isSuperAdmin}
                collapsed={collapsed}
              />
              <SidebarLink
                label="Integrations"
                icon={PlugZap}
                href="/integrations"
                isActive={isIntegrationsRoute}
                visible={isAdmin || isSuperAdmin}
                collapsed={collapsed}
              />
            </SidebarSection>
          )}
        </nav>
      </div>
    </div>
  )
}

interface DesktopSidebarContainerProps extends PropsWithChildren {
  collapsed?: boolean;
}

export function DesktopSidebarContainer({ children, collapsed = false }: DesktopSidebarContainerProps) {
  return (
    <div className={cn(
      "hidden md:block md:sticky md:top-0 md:h-screen flex-1 border-r bg-gray-50",
      collapsed ? "w-16" : "w-[190px]",
    )}>
      {children}
    </div>
  );
}

interface SidebarSectionProps extends PropsWithChildren {
  title: string;
  collapsed?: boolean;
}

function SidebarSection({ title, children, collapsed = false }: SidebarSectionProps) {
  return (
    <div className={cn("pb-4", collapsed && "w-full")}>
      <SidebarSectionHeader title={title} collapsed={collapsed} />
      <SidebarLinks>
        {children}
      </SidebarLinks>
    </div>
  )
}

interface SidebarSectionHeaderProps {
  title: string
  collapsed?: boolean;
}

function SidebarSectionHeader({ title, collapsed = false }: SidebarSectionHeaderProps) {
  return (
    <p className={cn(
      "h-8 px-3 text-[10px] leading-8 text-gray-500",
      collapsed && "text-transparent select-none"
    )}>
      {title}
    </p>
  )
}

interface SidebarLinksProps extends PropsWithChildren { }

function SidebarLinks({ children }: SidebarLinksProps) {
  return (
    <div>
      {children}
    </div>
  );
}

interface SidebarLinkProps {
  label: string;
  icon: LucideIcon;
  href: string;
  isActive: boolean;
  visible?: boolean;
  collapsed?: boolean;
  indicator?: boolean;
}

function SidebarLink({ label, icon, href, isActive, visible = true, collapsed = false, indicator = false }: SidebarLinkProps) {
  if (!visible) return null;

  const Icon = icon;
  return (
    <Link
      href={href}
      title={collapsed ? label : undefined}
      className={cn(
        linkBase,
        isActive ? linkActive : linkInactive,
        collapsed && "w-10 justify-center gap-0 px-0"
      )}
    >
      <span className="relative shrink-0">
        <Icon className={linkIcon} />
        {indicator && (
          <span
            aria-label="New Revenue Intelligence Signals"
            className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-[#9F86C0] ring-2 ring-gray-50"
          />
        )}
      </span>
      <span className={collapsed ? "sr-only" : undefined}>
        {label}
      </span>
    </Link>
  );
}
